import { useCallback, useEffect, useState } from "react";

import { api, type ReceiptSettlementReminderPreference } from "../lib/api";
import { logError } from "../lib/errors";

export function ReceiptSettlementReminderPreferenceCard() {
  const [preference, setPreference] = useState<ReceiptSettlementReminderPreference | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreference = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPreference(await api.getReceiptSettlementReminderPreference());
    } catch (cause) {
      logError(cause);
      setError("通知設定を読み込めませんでした。時間をおいてもう一度お試しください。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPreference();
  }, [loadPreference]);

  const isPaused = preference?.status === "paused";
  const updatePreference = async () => {
    if (!preference || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const nextPreference = isPaused
        ? await api.resumeReceiptSettlementReminder()
        : await api.pauseReceiptSettlementReminder();
      setPreference(nextPreference);
    } catch (cause) {
      logError(cause);
      setError("通知設定を変更できませんでした。時間をおいてもう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col items-start gap-1.5" aria-live="polite">
      {!loading && preference ? (
        <>
          <button
            type="button"
            onClick={() => void updatePreference()}
            disabled={submitting}
            className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-black text-[#A67C52] transition hover:bg-[#F5EDE8] disabled:opacity-50"
          >
            {submitting ? "変更中..." : isPaused ? "LINEリマインドを再開する" : "LINEリマインドを停止する"}
          </button>
          <p className="text-xs leading-5 text-[#9C867D]">
            {isPaused
              ? "現在、月末のLINEリマインドは停止中です。"
              : "未精算があると月末にLINEでお知らせします。不要な場合はここで止められます。"}
          </p>
        </>
      ) : null}

      {error ? (
        <p role="alert" className="text-xs font-bold text-[#B4533A]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
